import React, { useEffect, useState } from 'react';
import withComponentAuth from '../../hoc/withComponentAuth';
import { Box, Typography } from '@mui/material';
import { SmartFilter } from '../../components/common/SmartFilter';
import { CreateTableHeader } from '../../components/common/TableHeader';
import { headerAmcRequestsTable } from '../../utils/constants';
import Pagination from '../../components/atoms/Pagination';
import { IAMCRequest } from '../../utils/entities/interface';
import { debounce } from '../../utils/helper';
import { GETAmcRequests } from '../../services/requests';
import { AmcRequests } from '../../components/ui/list/AmcRequests';
import { useNavigate } from 'react-router-dom';
import AddAmcRequest from './AddAmcRequest';
import { usePaginationParams } from '../../hooks/usePaginationParams';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';

const ManageAMC = () => {
  const navigate = useNavigate();
  const { page, rowsPerPage, setPage, setRowsPerPage } = usePaginationParams();
  const [requests, setRequests] = useState<IAMCRequest[]>([]);
  const [totalRequests, setTotalRequest] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sort, setSort] = useState<'ASC' | 'DESC'>('ASC');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useScrollRestoration<HTMLDivElement>(!isLoading);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    fetchRequets(page, rowsPerPage, searchQuery, sort);
  }, [page, rowsPerPage, searchQuery, sort]);

  const fetchRequets = async (
    page: number,
    limit: number,
    searchQuery?: string,
    sort?: 'ASC' | 'DESC',
  ) => {
    try {
      setIsLoading(true);
      const response = await GETAmcRequests({
        page,
        limit,
        searchQuery,
        sortOrder: sort,
      });

      // Ensure requests is always an array
      setRequests(response?.data || []);
      setTotalRequest(response?.count || 0);
    } catch (error) {
      console.error('Failed to fetch technicians:', error);
      setRequests([]);
      setTotalRequest(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const refreshList = () => {
    fetchRequets(page, rowsPerPage, searchQuery, sort);
  };

  const handleSaveRefresh = () => {
    refreshList();
  };

  const handleFilterRefresh = () => {
    console.log('FILTER HAPPENED');
  };

  const handleOpenManageDrawer = (id: string | number) => {
    navigate(`/amc/request-details/${id}`);
  };
  // Debounced search handler
  const handleSearchRefresh = debounce((search: string) => {
    setSearchQuery(search);
    setPage(1);
  }, 500);

  const handleSortRefresh = () => {
    setSort(sort === 'ASC' ? 'DESC' : 'ASC');
    refreshList();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
      }}
    >
      <SmartFilter
        title="Amc Leads"
        createFilter={['SEARCH', 'ADD']}
        filterHeader={'Filter Technicians'}
        addbuttontitle="Add Request"
        onSaved={handleSaveRefresh}
        onFiltered={handleFilterRefresh}
        onSearch={handleSearchRefresh}
        onSort={handleSortRefresh}
        onNavigate={() => setAddOpen(true)}
      />

      <AddAmcRequest
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={handleSaveRefresh}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          p: 1,
        }}
      >
        {/* <CreateTableHeader tableCell={headerAmcRequestsTable} /> */}

        <Box
          ref={scrollRef}
          sx={{
            height: '100%',
            maxHeight: '100%',
            overflowY: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {/* FIXED: Check if requests is defined AND has length */}
          {!isLoading && (!requests || requests.length === 0) ? (
            <Typography
              textAlign={'center'}
              marginTop={'20%'}
              color="textSecondary"
            >
              No Requests Found
            </Typography>
          ) : (
            <AmcRequests
              requests={requests || []}
              isLoading={isLoading}
              onUpdate={refreshList}
              tableCell={headerAmcRequestsTable}
              onManageClick={handleOpenManageDrawer}
            />
          )}
        </Box>

        <Box display={'block'} bottom={0} right={0} mt={2}>
          <Pagination
            count={totalRequests}
            page={page - 1}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default withComponentAuth(ManageAMC);
